'use client';

/**
 * KanbanCard Component
 *
 * A draggable card for use within KanbanColumn. Uses @dnd-kit for drag-and-drop
 * functionality with glassmorphism styling and optional SLA indicators.
 */

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Box from '@mui/material/Box';
import { styled, useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import type React from 'react';
import type { NeonAccent } from '../../theme/palette';
import { getNeonStyles } from '../../theme/palette';
import { createNeonGlow, glassEffects } from '../../theme/shadows';
import { durations, easings, transitionPresets } from '../../theme/transitions';
import type { KanbanItem } from '../../types/pipeline';
import { SLAIndicator } from './SLAIndicator';

// ============================================================================
// Types
// ============================================================================

export interface KanbanCardProps<T = unknown> {
  /** The kanban item data */
  item: KanbanItem<T>;
  /** Render function for card content */
  renderContent: (item: KanbanItem<T>) => React.ReactNode;
  /** Click handler */
  onClick?: () => void;
  /** Neon accent color */
  neonAccent?: NeonAccent;
  /** Whether to show SLA indicator */
  showSLA?: boolean;
  /** Whether currently dragging */
  isDragging?: boolean;
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledCardProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
  $isDragging: boolean;
  $isOver: boolean;
}

const StyledCard = styled(motion.div, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledCardProps>(({ $isDark, $neonAccent, $isDragging, $isOver }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  // Base styles
  const baseStyles = {
    position: 'relative' as const,
    borderRadius: '12px',
    padding: '12px',
    cursor: 'grab',
    transition: transitionPresets.card,
    touchAction: 'none',
    userSelect: 'none' as const,
  };

  // Dragging state
  if ($isDragging) {
    return {
      ...baseStyles,
      background: neonStyles ? neonStyles.background : glass.background,
      border: `1px solid ${neonStyles ? neonStyles.border : glass.border}`,
      boxShadow: neonStyles
        ? `${createNeonGlow($neonAccent!, 'strong')}, 0 20px 40px rgba(0,0,0,0.3)`
        : `${glass.shadowHover}, 0 20px 40px rgba(0,0,0,0.3)`,
      opacity: 0.95,
      cursor: 'grabbing',
      zIndex: 1000,
      transform: 'scale(1.02) rotate(2deg)',
    };
  }

  // Neon accent styling
  if (neonStyles && $neonAccent) {
    return {
      ...baseStyles,
      background: neonStyles.background,
      border: `1px solid ${neonStyles.border}`,
      boxShadow: 'none',

      '&:hover': {
        boxShadow: createNeonGlow($neonAccent, 'subtle'),
        transform: 'translateY(-2px)',
      },

      '&:active': {
        cursor: 'grabbing',
      },
    };
  }

  // Default glass styling
  return {
    ...baseStyles,
    background: glass.background,
    backdropFilter: glass.backdropBlur,
    border: `1px solid ${glass.border}`,
    boxShadow: $isOver ? glass.shadowHover : 'none',

    '&:hover': {
      background: glass.backgroundHover,
      border: `1px solid ${glass.borderHover}`,
      boxShadow: glass.shadowHover,
      transform: 'translateY(-2px)',
    },

    '&:active': {
      cursor: 'grabbing',
    },
  };
});

const CardContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

const SLABadgeWrapper = styled(Box)({
  position: 'absolute',
  top: '8px',
  right: '8px',
});

// ============================================================================
// Component
// ============================================================================

export function KanbanCard<T>({
  item,
  renderContent,
  onClick,
  neonAccent,
  showSLA = true,
  isDragging: externalIsDragging,
}: KanbanCardProps<T>) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: sortableIsDragging,
    isOver,
  } = useSortable({
    id: item.id,
    data: {
      type: 'card',
      item,
    },
  });

  const isDragging = externalIsDragging ?? sortableIsDragging;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || `transform ${durations.fast}ms ${easings.smooth}`,
  };

  const handleClick = () => {
    // Only trigger click if not dragging
    if (!isDragging && onClick) {
      onClick();
    }
  };

  return (
    <StyledCard
      ref={setNodeRef}
      style={style}
      $isDark={isDark}
      $neonAccent={neonAccent}
      $isDragging={isDragging}
      $isOver={isOver}
      onClick={handleClick}
      {...attributes}
      {...listeners}
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10, scale: 0.95 }}
      transition={{
        duration: durations.fast / 1000,
        ease: [0.215, 0.61, 0.355, 1],
      }}
    >
      {showSLA && item.sla && (
        <SLABadgeWrapper>
          <SLAIndicator
            status={item.sla.status}
            remainingHours={item.sla.remainingHours}
            targetHours={24}
            size="small"
            showCountdown
          />
        </SLABadgeWrapper>
      )}

      <CardContent sx={{ pr: showSLA && item.sla ? '60px' : 0 }}>{renderContent(item)}</CardContent>
    </StyledCard>
  );
}

KanbanCard.displayName = 'KanbanCard';

export default KanbanCard;
